(() => {
  'use strict';

  if (window.__tifelowSwishInstalled) return;
  window.__tifelowSwishInstalled = true;

  const DURATION = 900;
  const LOGO_URL = window.TIFELOW_SWISH_LOGO_URL;
  let transitioning = false;

  const style = document.createElement('style');
  style.textContent = `
    .tifelow-swish-overlay {
      position: fixed; inset: 0; z-index: 2147483000;
      pointer-events: none; overflow: hidden; background: transparent;
      isolation: isolate;
    }
    .tifelow-swish-preview {
      position: fixed; inset: 0; z-index: 1; overflow: hidden;
      overflow-y: auto; background: var(--bg, #101010);
      color: var(--text, #f4f2ed); pointer-events: none;
      clip-path: polygon(100% 0, 100% 100%, 100% 100%, 100% 0);
      contain: paint;
    }
    .tifelow-swish-preview > .tifelow-swish-preview-content {
      min-height: 100%; width: 100%;
    }
    .tifelow-swish-preview video { max-width: 100%; }
    .tifelow-swish-logo {
      position: absolute; z-index: 2; width: min(43vw, 620px); height: auto;
      max-height: 62vh; object-fit: contain; left: 100vw; top: 50%;
      transform: translateY(-50%); pointer-events: none; user-select: none;
      filter: drop-shadow(0 8px 24px rgba(0,0,0,.24));
      will-change: left, filter, transform;
    }
    @media (max-width: 700px) {
      .tifelow-swish-logo { width: 78vw; max-height: 48vh; }
    }
  `;
  document.head.appendChild(style);

  function isCartOrCheckout(url) {
    const path = url.pathname.replace(/\/+$/, '') || '/';
    return /\/(cart|checkout)(\/|$)/i.test(path) ||
      /\/cart\/(add|change|update|clear)(\.js)?$/i.test(path);
  }

  function shouldHandleLink(link, event) {
    if (!link || event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return false;
    if (link.hasAttribute('data-no-transition') || link.closest('[data-no-transition]')) return false;

    const raw = link.getAttribute('href');
    if (!raw || raw.trim().startsWith('#') ||
        /^(mailto:|tel:|javascript:|blob:|data:)/i.test(raw.trim())) return false;

    let url;
    try { url = new URL(raw, window.location.href); } catch (_) { return false; }
    if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) return false;
    if (isCartOrCheckout(url)) return false;
    if (url.pathname === window.location.pathname &&
        url.search === window.location.search) return false;
    if (url.pathname.startsWith('/cdn/') || url.pathname.startsWith('/admin')) return false;
    return true;
  }

  function makePreview(html, responseUrl) {
    const finalUrl = new URL(responseUrl, window.location.href);
    if (finalUrl.origin !== window.location.origin) return null;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    if (!parsed || !parsed.body || !parsed.documentElement) return null;
    const contentType = parsed.contentType;
    if (contentType && contentType !== 'text/html') return null;

    const preview = document.createElement('div');
    preview.className = 'tifelow-swish-preview';
    const bodyClasses = (parsed.body.className || '').split(/\s+/).filter(Boolean);
    bodyClasses.forEach(cls => preview.classList.add(cls));

    const content = document.createElement('div');
    content.className = 'tifelow-swish-preview-content';

    // Copy the destination page's visible body only. Scripts are deliberately
    // excluded so the preview cannot run Shopify/theme actions a second time.
    Array.from(parsed.body.childNodes).forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE &&
          (node.tagName === 'SCRIPT' || node.tagName === 'BASE')) return;
      const clone = node.cloneNode(true);
      if (clone.nodeType === Node.ELEMENT_NODE) {
        clone.querySelectorAll('script, base').forEach(el => el.remove());
        clone.querySelectorAll('a, button, input, select, textarea').forEach(el => {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-hidden', 'true');
        });
      }
      content.appendChild(clone);
    });

    preview.appendChild(content);
    return preview;
  }

  async function fetchPreview(destination) {
    try {
      const response = await fetch(destination, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow',
        headers: { 'Accept': 'text/html' },
        cache: 'no-store'
      });
      if (!response.ok) return null;
      const type = response.headers.get('content-type') || '';
      if (!/text\/html/i.test(type)) return null;
      if (new URL(response.url, window.location.href).origin !== window.location.origin) return null;
      const html = await response.text();
      if (!html || !/<html[\s>]/i.test(html)) return null;
      return makePreview(html, response.url);
    } catch (_) {
      // The swish still runs; the browser will navigate normally at the end.
      return null;
    }
  }

  function runSwish(destination, previewPromise) {
    if (transitioning) return;
    transitioning = true;

    const overlay = document.createElement('div');
    overlay.className = 'tifelow-swish-overlay';
    overlay.setAttribute('aria-hidden', 'true');

    const logo = document.createElement('img');
    logo.className = 'tifelow-swish-logo';
    logo.alt = '';
    logo.draggable = false;
    if (LOGO_URL) logo.src = LOGO_URL;
    overlay.appendChild(logo);
    document.body.appendChild(overlay);

    const startedAt = performance.now();
    let previewAdded = false;
    let finished = false;
    let safetyTimer = null;

    previewPromise.then(preview => {
      if (!preview || finished || !overlay.isConnected) return;
      previewAdded = true;
      overlay.insertBefore(preview, logo);
      // The next animation frame updates the reveal to the current elapsed time,
      // so a slower response does not extend the transition beyond 900 ms.
    });

    function finish() {
      if (finished) return;
      finished = true;
      if (safetyTimer !== null) window.clearTimeout(safetyTimer);
      window.location.assign(destination);
    }

    function step(now) {
      if (finished) return;
      const p = Math.min(1, Math.max(0, (now - startedAt) / DURATION));
      const eased = p < 0.5
        ? 4 * p * p * p
        : 1 - Math.pow(-2 * p + 2, 3) / 2;
      const vw = window.innerWidth;
      const logoWidth = Math.min(vw * (vw <= 700 ? 0.78 : 0.43), vw <= 700 ? vw * 0.78 : 620);
      logo.style.left = (vw + (-logoWidth - vw) * eased) + 'px';
      logo.style.transform = `translateY(-50%) rotate(${(1 - eased) * -3.5}deg)`;
      logo.style.filter = `blur(${(1 - eased) * 0.7}px) drop-shadow(0 8px 24px rgba(0,0,0,.24))`;

      if (previewAdded) {
        const boundary = 100 * (1 - eased);
        overlay.querySelector('.tifelow-swish-preview').style.clipPath =
          `polygon(${boundary}% 0, 100% 0, 100% 100%, ${boundary}% 100%)`;
      }

      if (p < 1) requestAnimationFrame(step);
      else finish();
    }

    // If animation frames are paused in a background tab, still avoid a stuck page.
    safetyTimer = window.setTimeout(finish, DURATION + 180);
    requestAnimationFrame(step);
  }

  document.addEventListener('click', event => {
    const target = event.target;
    const link = target && target.closest ? target.closest('a[href]') : null;
    if (!shouldHandleLink(link, event)) return;

    const destination = new URL(link.href, window.location.href);
    event.preventDefault();
    const previewPromise = fetchPreview(destination.href);
    runSwish(destination.href, previewPromise);
  }, true);
})();
